// @ts-nocheck
import React, { useState, useRef } from "react";
import { Button, Input, useToast } from "@/shared/components/ui";
import { useEscapeKey } from "@/shared/hooks/useEscapeKey";

export function ForgotPassword({ onBack }) {
  useEscapeKey(onBack);
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);
  const [codeError, setCodeError] = useState("");
  // Monotonic id so only the latest OTP verification request applies its result;
  // any in-flight response for an older code is ignored (prevents races when the
  // user quickly edits the 6-digit code).
  const verifySeqRef = useRef(0);
  const toast = useToast();

  // Verify the OTP as soon as 6 digits are entered, before the new password.
  const verifyCode = async (value) => {
    const seq = ++verifySeqRef.current;
    setVerifying(true);
    setCodeError("");
    setCodeVerified(false);
    try {
      const res = await fetch("/api/auth/reset-password/confirm", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), code: value }),
      });
      const data = await res.json().catch(() => ({}));
      if (seq !== verifySeqRef.current) return; // superseded by a newer entry
      if (!res.ok) {
        setCodeVerified(false);
        setCodeError(data.error || "Incorrect code");
        return;
      }
      setCodeVerified(true);
    } catch (e) {
      if (seq !== verifySeqRef.current) return;
      setCodeError("Cannot reach server. Please try again.");
    } finally {
      if (seq === verifySeqRef.current) setVerifying(false);
    }
  };

  const onCodeChange = (raw) => {
    const value = String(raw).replace(/\D/g, "").slice(0, 6);
    // Invalidate any in-flight verification for the previous code value.
    verifySeqRef.current += 1;
    setCode(value);
    setCodeVerified(false);
    setCodeError("");
    setVerifying(false);
    if (value.length === 6) void verifyCode(value);
  };

  const requestCode = async () => {
    if (!email.trim()) {
      toast.show({ tone: "warning", message: "Enter your email." });
      return;
    }
    setBusy(true);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 404) {
        toast.show({ tone: "danger", title: "Email not found", message: "No account found for this email." });
        return;
      }
      if (!res.ok) {
        toast.show({ tone: "danger", message: data.error || "Could not send the code. Try again." });
        return;
      }
      setStep(2);
    } catch (e) {
      toast.show({ tone: "danger", title: "Connection error", message: "Cannot reach server. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async () => {
    if (!/^\d{6}$/.test(code.trim())) {
      toast.show({ tone: "warning", message: "Enter the 6-digit code from your email." });
      return;
    }
    if (!codeVerified) {
      toast.show({ tone: "warning", message: "Please verify the code first." });
      return;
    }
    if (newPassword.length < 8) {
      toast.show({ tone: "warning", message: "New password must be at least 8 characters." });
      return;
    }
    if (newPassword !== confirm) {
      toast.show({ tone: "warning", message: "Passwords do not match." });
      return;
    }
    setBusy(true);
    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          code: code.trim(),
          newPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.show({ tone: "danger", message: data.error || "Could not reset password." });
        return;
      }
      setStep(3);
    } catch (e) {
      toast.show({ tone: "danger", title: "Connection error", message: "Cannot reach server. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-dvh safe-bottom bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <button
          type="button"
          onClick={onBack}
          className="text-ink-400 hover:text-ink-900 mb-6 text-sm"
        >
          ← Back to Login
        </button>
        <div className="bg-white border border-line rounded-2xl p-4 sm:p-8 shadow-sm">
          <div className="w-12 h-12 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-center mb-4 text-2xl">
            🔒
          </div>

          {step === 1 && (
            <>
              <h1 className="text-lg sm:text-2xl font-black text-ink-900 mb-2">Forgot password</h1>
              <p className="text-ink-400 text-sm leading-relaxed mb-5">
                Enter your registered email and we'll send a 6-digit code to reset your password.
              </p>
              <label className="block text-xs font-semibold text-ink-600 uppercase tracking-wide mb-2">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && requestCode()}
                placeholder="your@email.com"
                className="mb-4"
              />
              <Button variant="primary" fullWidth onClick={requestCode} disabled={busy || !email.trim()} loading={busy}>
                {busy ? "Sending code…" : "Send code →"}
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              <h1 className="text-lg sm:text-2xl font-black text-ink-900 mb-2">Enter code</h1>
              <p className="text-ink-400 text-sm leading-relaxed mb-5">
                We sent a 6-digit code to <span className="text-ink-900 font-semibold">{email}</span>. It expires in 10 minutes.
              </p>
              <label className="block text-xs font-semibold text-ink-600 uppercase tracking-wide mb-2">Temporary code</label>
              <Input
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => onCodeChange(e.target.value)}
                placeholder="123456"
                className={`tracking-[0.4em] text-center font-bold ${
                  codeError
                    ? "border-red-400 focus:border-red-400"
                    : codeVerified
                      ? "border-emerald-400 focus:border-emerald-400"
                      : ""
                }`}
              />
              <div className="min-h-[20px] mt-1 mb-3 text-xs font-semibold">
                {verifying && <span className="text-ink-400">Verifying code…</span>}
                {!verifying && codeError && <span className="text-red-600">{codeError}</span>}
                {!verifying && !codeError && codeVerified && (
                  <span className="text-emerald-600">Code verified ✓</span>
                )}
              </div>

              {codeVerified && (
                <>
                  <label className="block text-xs font-semibold text-ink-600 uppercase tracking-wide mb-2">New password</label>
                  <div className="relative mb-4">
                    <Input
                      type={showPw ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      className="pr-16"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-900 text-xs font-bold"
                    >
                      {showPw ? "HIDE" : "SHOW"}
                    </button>
                  </div>
                  <label className="block text-xs font-semibold text-ink-600 uppercase tracking-wide mb-2">Confirm password</label>
                  <Input
                    type={showPw ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submitReset()}
                    placeholder="Re-enter new password"
                    className="mb-4"
                  />
                  <Button variant="primary" fullWidth onClick={submitReset} disabled={busy || !newPassword || !confirm} loading={busy}>
                    {busy ? "Changing password…" : "Change password →"}
                  </Button>
                </>
              )}
              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setCode("");
                  setCodeVerified(false);
                  setCodeError("");
                  setNewPassword("");
                  setConfirm("");
                }}
                className="w-full mt-3 text-ink-400 hover:text-ink-900 text-sm"
              >
                Use a different email
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <h1 className="text-lg sm:text-2xl font-black text-ink-900 mb-2">Password changed ✓</h1>
              <p className="text-ink-400 text-sm leading-relaxed mb-6">
                Your password has been updated. You can now sign in with your new password.
              </p>
              <Button variant="primary" fullWidth onClick={onBack}>
                Back to Login →
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
